import React from 'react';
import { useTranslation } from 'react-i18next';

type UseDialogConfirmParams = Readonly<{
  buttonTextCancel?: string;
  buttonTextSuccess?: string;
  children?: React.ReactNode;
  detailedList?: ReadonlyArray<React.ReactNode>;
  hasCancelButton?: boolean;
  iconName?: string;
  onCancel?: () => void;
  onClose: () => void;
}>;

type UseDialogConfirmResult = Readonly<{
  hasChildren: boolean;
  hasDetailedList: boolean;
  resolvedButtonTextCancel: string;
  resolvedButtonTextSuccess: string;
  resolvedIconName: string;
  resolvedOnCancel: (() => void) | undefined;
}>;

const DEFAULT_ICON_NAME = 'warning';

function useDialogConfirm({
  buttonTextCancel,
  buttonTextSuccess,
  children,
  detailedList,
  hasCancelButton = true,
  iconName,
  onCancel,
  onClose,
}: UseDialogConfirmParams): UseDialogConfirmResult {
  const { t } = useTranslation(['actions']);

  const resolvedButtonTextSuccess = buttonTextSuccess ?? t('actions:Delete');
  const resolvedButtonTextCancel = buttonTextCancel ?? t('actions:Cancel');
  const resolvedIconName = iconName ?? DEFAULT_ICON_NAME;
  // Si el consumidor no provee onCancel y el botón de cancelar está habilitado,
  // el botón Cancel debe cerrar el dialog igualmente; reutilizamos onClose como
  // fallback seguro. Si `hasCancelButton=false`, devolvemos undefined para que
  // Dialog v2 oculte el botón Cancel por completo.
  const resolvedOnCancel = hasCancelButton ? (onCancel ?? onClose) : undefined;

  const hasDetailedList = Array.isArray(detailedList) && detailedList.length > 0;
  const hasChildren = Boolean(children);

  return {
    hasChildren,
    hasDetailedList,
    resolvedButtonTextCancel,
    resolvedButtonTextSuccess,
    resolvedIconName,
    resolvedOnCancel,
  };
}

export default useDialogConfirm;
